import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { client, MarketOrderType, Instance, Market } from '../../app/services/api/orderManagementClient';
import { convertISODurationToTimeInput, convertTimeInputToISODuration } from '../../utils/timeUtils';
import useLogError from '../../hooks/useLogError';

const OrderTypeForm = () => {
  const { t } = useTranslation('orderTypes');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  
  const [formData, setFormData] = useState<MarketOrderType>({
    companyOrderTypeId: 0,
    instanceName: '',
    companyId: 0,
    orderTypeId: 0,
    name: '',
    marketName: '',
    customerTypeId: undefined,
    internal: false,
    external: false,
    code: '',
    cutoff: {
      customerTypeOrderTypeId: undefined,
      orderCutOffTime: '',
      canPushOrderInEnteredDOS: false,
      supplierName: '',
      supplierEmailAddress: '',
      supplierEmailAddress2: '',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchMarkets = async (companyId: number) => {
    try {
      const result = await client.getMarkets({
        companyId: companyId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (result.success && result.data) {
        setMarkets(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching markets:', error);
      setMarkets([]);
    }
  };

  const fetchOrderType = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await client.getMarketOrderType(id);
      if (result.success && result.data) {
        setFormData(result.data);
        if (result.data.companyId) {
          fetchMarkets(result.data.companyId);
        }
      }
    } catch (error) {
      console.error('Error fetching order type:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
    if (isEdit) {
      fetchOrderType();
    }
  }, [id, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketOrderType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | number = event.target.value;
    
    if (field === 'orderTypeId') {
      // Handle empty string to maintain the ability to clear the field
      value = value === '' ? 0 : parseInt(value) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleCutoffChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    let value: string | boolean = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    if (field === 'orderCutOffTime' && typeof value === 'string') {
      const isoDuration = convertTimeInputToISODuration(value);
      value = isoDuration || '';
    }
    
    setFormData(prev => ({
      ...prev,
      cutoff: {
        ...prev.cutoff,
        [field]: value,
      },
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: undefined,
      marketName: '',
    }));
    
    if (value?.id) {
      fetchMarkets(value.id);
    } else {
      setMarkets([]);
    }
    
    if (errors.companyId) {
      setErrors(prev => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    setFormData(prev => ({
      ...prev,
      customerTypeId: value?.customerTypeId,
      marketName: value?.name || '',
    }));
  };

  const handleCheckboxChange = (field: keyof MarketOrderType) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketOrderType(id!, formData);
        if (result.success) {
          setMessage(t('updateSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/order-types${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      } else {
        const result = await client.createMarketOrderType(formData);
        if (result.success) {
          setMessage(t('createSuccessMessage'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/order-types${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        } else if (result.message) {
          setMessage(result.message);
          setIsSuccess(false);
          setShowMessage(true);
        }
      }
    } catch (error) {
      logError(error);
      const errorMessage = extractErrorMessage(error);
      setMessage(errorMessage || (isEdit ? t('updateErrorMessage') : t('createErrorMessage')));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/order-types${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'OrderTypeUpdate' : 'OrderTypeCreate', 'ConfigurationAdmin']);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEdit ? 'editOrderType' : 'createOrderType')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack spacing={8}>
        <PageTitle>{t(isEdit ? 'editOrderType' : 'createOrderType')}</PageTitle>

        {/* Basic Order Type Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('basicInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={6}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('orderTypeName')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('orderTypeId')}
                  type="number"
                  value={formData.orderTypeId !== undefined ? formData.orderTypeId.toString() : ''}
                  onChange={handleInputChange('orderTypeId')}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('code')}
                  value={formData.code}
                  onChange={handleInputChange('code')}
                  error={!!errors.code}
                  helperText={errors.code}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find(instance => instance.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      error={!!errors.companyId}
                      helperText={errors.companyId}
                      required
                    />
                  )}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Autocomplete
                  options={markets}
                  getOptionLabel={(option) => option.name}
                  value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                  onChange={handleMarketChange}
                  disabled={!formData.companyId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('market')}
                      helperText={t('marketOptional')}
                    />
                  )}
                />
              </Grid>
              <Grid item
                xs={12}>
                <Stack direction="row"
                  spacing={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.internal}
                        onChange={handleCheckboxChange('internal')}
                      />
                    }
                    label={t('internal')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.external}
                        onChange={handleCheckboxChange('external')}
                      />
                    }
                    label={t('external')}
                  />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Order Cutoff Information Card */}
        <Card elevation={1}>
          <CardHeader
            title={t('orderCutoffInformation')}
            sx={{ color: 'text.primary' }}
          />
          <CardContent sx={{ p: 8 }}>
            <Grid container
              spacing={6}>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('orderCutoffTime')}
                  type="time"
                  value={convertISODurationToTimeInput(formData?.cutoff?.orderCutOffTime || '')}
                  onChange={handleCutoffChange('orderCutOffTime')}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData?.cutoff?.canPushOrderInEnteredDOS || false}
                      onChange={handleCutoffChange('canPushOrderInEnteredDOS')}
                    />
                  }
                  label={t('canPushOrderInEnteredDOS')}
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierName')}
                  value={formData?.cutoff?.supplierName || ''}
                  onChange={handleCutoffChange('supplierName')}
                  error={!!errors.supplierName}
                  helperText={errors.supplierName}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierEmailAddress')}
                  type="email"
                  value={formData?.cutoff?.supplierEmailAddress || ''}
                  onChange={handleCutoffChange('supplierEmailAddress')}
                  error={!!errors.supplierEmailAddress}
                  helperText={errors.supplierEmailAddress}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <TextField
                  label={t('supplierEmailAddress2')}
                  type="email"
                  value={formData?.cutoff?.supplierEmailAddress2 || ''}
                  onChange={handleCutoffChange('supplierEmailAddress2')}
                  fullWidth
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons Card */}
        <Card
          elevation={0}
          sx={{ backgroundColor: 'background.default' }}>
          <CardContent sx={{ p: 8 }}>
            <Stack direction="row"
              spacing={3}
              justifyContent="flex-end">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleCancel}
              >
                {t('cancel')}
              </Button>
              {userCanSave && (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSaving}
                >
                  {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default OrderTypeForm;
